"use client";

import { useEffect, useRef } from "@tiendanube/nube-sdk-react-runtime";
import { Slot } from "../slot";

export type BarProps = Readonly<{
	top?: boolean;
	bottom?: boolean;
	sticky?: boolean;
	children: React.ReactNode;
}>;

type BarSide = "top" | "bottom";

const baseStyle: React.CSSProperties = {
	position: "fixed",
	zIndex: 9998, // above edges (9997) and corners (9996), below #nube-modal (9999)
};

/**
 * CSS variable holding the current height of the sticky bar on `side`, set on
 * `<html>` so themes can offset their own fixed elements (e.g. a fixed header).
 */
export function stickyBarHeightVar(side: BarSide) {
	return `--nube-sdk-bar-${side}-sticky-height`;
}

/**
 * Keeps `<body>` padded by the height of the sticky bar on `side`, so the
 * store content is pushed away from the bar instead of sitting under it. The
 * padding is added on top of whatever the theme already sets, and is removed
 * when the bar is empty or unmounts.
 */
function useStickyBarOffset(
	ref: { current: HTMLDivElement | null },
	side: BarSide | undefined,
) {
	useEffect(() => {
		const el = ref.current;
		if (!side || !el || typeof ResizeObserver === "undefined") return;

		const body = document.body;
		const root = document.documentElement;
		const prop = side === "top" ? "paddingTop" : "paddingBottom";
		const cssVar = stickyBarHeightVar(side);
		const originalInline = body.style[prop];

		const apply = () => {
			const height = el.getBoundingClientRect().height;
			// Read the theme's own padding without our offset applied.
			body.style[prop] = originalInline;
			if (height > 0) {
				const base = Number.parseFloat(getComputedStyle(body)[prop]) || 0;
				body.style[prop] = `${base + height}px`;
			}
			root.style.setProperty(cssVar, `${height}px`);
		};

		const observer = new ResizeObserver(apply);
		observer.observe(el);
		apply();

		return () => {
			observer.disconnect();
			body.style[prop] = originalInline;
			root.style.removeProperty(cssVar);
		};
	}, [side]);
}

export function Bar({ top, bottom, sticky, children }: BarProps) {
	const ref = useRef<HTMLDivElement>(null);
	const side: BarSide | undefined = top ? "top" : bottom ? "bottom" : undefined;
	useStickyBarOffset(ref, sticky ? side : undefined);

	const style: React.CSSProperties = { ...baseStyle };

	if (side) {
		// Non-sticky bars sit just inside the sticky bar on the same side.
		const offset = sticky ? 0 : `var(${stickyBarHeightVar(side)}, 0px)`;
		style[side] = offset;
		style.left = 0;
		style.right = 0;
		style.maxHeight = "15vh";
	}

	return (
		<div ref={ref} style={style}>
			{children}
		</div>
	);
}

export function FloatingBars() {
	return (
		<>
			<Bar top>
				<Slot id="bar_top" />
			</Bar>
			<Bar bottom>
				<Slot id="bar_bottom" />
			</Bar>
			<Bar top sticky>
				<Slot id="bar_top_sticky" />
			</Bar>
			<Bar bottom sticky>
				<Slot id="bar_bottom_sticky" />
			</Bar>
		</>
	);
}
